import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import type { PlayerStatsVenue } from '@api-service/player-stats';
import {
  BASIS_OPTIONS,
  VENUE_OPTIONS,
  WINDOW_OPTIONS,
  parseSeasonParam,
  type FormWindow,
  type ProfileFilters,
  type StatsBasis
} from '@lib/page-utils/player-profile/filters';

export const DEFAULT_VENUE: PlayerStatsVenue = 'all';
export const DEFAULT_WINDOW: FormWindow = 'season';
export const DEFAULT_BASIS: StatsBasis = 'per90';

const readQueryValue = (value: string | string[] | undefined): string | null =>
  typeof value === 'string' ? value : null;

const parseOption = <T extends string>(
  raw: string | null,
  options: { value: T }[],
  fallback: T
): T => {
  const match = options.find((option) => option.value === raw);
  return match ? match.value : fallback;
};

export type UsePlayerProfileFiltersResult = {
  filters: ProfileFilters;
  setFilter: <K extends keyof ProfileFilters>(
    key: K,
    value: ProfileFilters[K]
  ) => void;
};

/**
 * Filter state for the profile, mirrored to the URL query so a filtered view is
 * shareable and the back button steps through filter changes rather than
 * leaving the page. Values are written shallowly — nothing here needs a
 * server round trip.
 *
 * `defaultSeason` is the season shown when the URL names none: the current
 * season in season, and the season just finished pre-season, when the incoming
 * season has no data yet.
 */
export const usePlayerProfileFilters = (
  defaultSeason: number | null
): UsePlayerProfileFiltersResult => {
  const router = useRouter();

  const filters = useMemo<ProfileFilters>(() => {
    return {
      season: parseSeasonParam(
        readQueryValue(router.query.season),
        defaultSeason ?? 0
      ),
      venue: parseOption(
        readQueryValue(router.query.venue),
        VENUE_OPTIONS,
        DEFAULT_VENUE
      ),
      window: parseOption(
        readQueryValue(router.query.window),
        WINDOW_OPTIONS,
        DEFAULT_WINDOW
      ),
      basis: parseOption(
        readQueryValue(router.query.basis),
        BASIS_OPTIONS,
        DEFAULT_BASIS
      )
    };
  }, [
    router.query.season,
    router.query.venue,
    router.query.window,
    router.query.basis,
    defaultSeason
  ]);

  const setFilter = useCallback<UsePlayerProfileFiltersResult['setFilter']>(
    (key, value) => {
      const nextQuery = { ...router.query, [key]: String(value) };

      // The chip for the default season is the default view, so it stays out
      // of the URL and keeps the canonical player URL clean.
      if (key === 'season' && value === defaultSeason) {
        delete nextQuery.season;
      }

      router.replace(
        { pathname: router.pathname, query: nextQuery },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [router, defaultSeason]
  );

  return { filters, setFilter };
};
